'use es6';

import { Component, Fragment } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { NavMarker } from 'react-rhumb';
import CoreEditorComponents from 'ContentEditorUI/containers/CoreEditorComponents';
import ApplicationContainer from 'ContentEditorUI/containers/ApplicationContainer';
import ExitLayoutModeButtonContainer from 'ContentEditorUI/containers/ExitLayoutModeButtonContainer';
import ErrorPagePortal from 'ContentEditorUI/components/app/ErrorPagePortal';
import AsyncComponentContext from 'ContentEditorUI/context/AsyncComponentContext';
import AddSectionModuleSchemaMissingErrorModal from 'ContentEditorUI/containers/sections/AddSectionModuleSchemaMissingErrorModal';
import { getHasStagingDomain } from 'ContentEditorUI/redux/selectors/contentSchemaSelectors';
import { getHasAnyContainerOrDndAreaToAddModules } from 'ContentEditorUI/redux/selectors/moduleSelectors';
import { getHasContentModel } from 'ContentEditorUI/redux/selectors/baseContentModelSelectors';
import { getAddSectionMissingModuleSchemaErrors } from 'ContentEditorUI/redux/selectors/sectionsSelectors';
import { getAppStatus, getIframeReady, getAddModuleSidebarLoaded } from 'ContentEditorUI/redux/selectors/appStatusSelectors';
import { getIsShowingCreateNewSectionPanel, getShowDeleteSectionModal, getShowSectionsLibraryModal } from 'ContentEditorUI/redux/selectors/sectionsSelectors';
import { getOpenModal } from 'ContentEditorUI/redux/selectors/modalSelectors';
import { MODAL_TYPES } from 'ContentEditorUI/lib/modalConstants';
import { closeModal } from 'ContentEditorUI/redux/actions/appActions';
import UsageTracker from 'ContentEditorUI/utils/UsageTracker';
import { fetchModuleSchemas as fetchModuleSchemasActionCreator } from 'ContentEditorUI/redux/actions/moduleSchemaActions';
import FullScreenEditorWrapper from 'ContentEditorUI/containers/FullScreenEditorWrapper';
import { APP_STATUS } from 'ContentEditorUI/redux/constants';
import { trackAppLifecycleTimestamp, WHOLE_APP_UX_READY, PARENT_APP_UX_READY, SIDEBAR_IFRAME_UX_READY, PREVIEW_IFRAME_UX_READY } from 'ContentEditorUI/utils/lifecycleData';
import SidebarIframeStylesManager from 'ContentEditorUI/utils/SidebarIframeStylesManager';
import PreviewIframeStylesManager from 'ContentEditorUI/utils/PreviewIframeStylesManager';
import { getPreviewIframeWrapperElement, getSidebarIframeWrapperElement } from 'ContentEditorUI/lib/layoutDom';
import { getIsReadOnlyMode } from 'ContentEditorUI/redux/selectors/appStatusSelectors';
import EditorConfigSingleton from '../EditorConfigSingleton';
import ContentAssistanceRequestDelegate from 'ContentEditorUI/containers/ContentAssistanceRequestDelegate';
import CollaborationSidebarWithWrapper from '../components/CollaborationSidebarWithWrapper';
import { Flex } from 'foundations-components/components/Flex';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const mapStateToProps = state => {
  const getHasPermissionsToLoadEditor = EditorConfigSingleton.getIn(['redux', 'selectors', 'getHasPermissionsToLoadEditor']);
  const hasIframe = EditorConfigSingleton.getHasIframe();
  const supportsSections = EditorConfigSingleton.getFeatureFlagOrDefault('sections');
  const openModal = getOpenModal(state);
  return {
    appStatus: getAppStatus(state),
    hasStagingDomain: getHasStagingDomain(state),
    // Since the page permission check requires the internal/edit enpoint to be back, passing back
    // an indeterminate `null` value `hasRequiredAccess` until the API is available
    hasRequiredAccess: !getHasContentModel(state) ? null : getHasPermissionsToLoadEditor(state),
    iframeReady: getIframeReady(state),
    addModuleSidebarLoaded: getAddModuleSidebarLoaded(state),
    shouldHaveAddModuleSidebar: getHasAnyContainerOrDndAreaToAddModules(state),
    isReadOnlyMode: getIsReadOnlyMode(state),
    shouldShowCreateNewSectionPanel: getIsShowingCreateNewSectionPanel(state),
    showDeleteSectionModal: getShowDeleteSectionModal(state),
    showSectionsLibraryModal: getShowSectionsLibraryModal(state),
    hasIframe,
    supportsSections,
    isSectionsInstructionalModalOpen: openModal === MODAL_TYPES.SECTIONS_INSTRUCTIONAL_MODAL,
    addSectionMissingModuleSchemaErrors: getAddSectionMissingModuleSchemaErrors(state)
  };
};
const mapDispatchToProps = {
  fetchModuleSchemas: fetchModuleSchemasActionCreator,
  closeModal
};
class UniversalApplicationContainer extends Component {
  constructor(props) {
    super(props);
    this.state = {
      isAppFullyReady: false
    };
    if (props.hasIframe) {
      SidebarIframeStylesManager.init(getSidebarIframeWrapperElement(), document.querySelector('iframe#contentEditor-sidebar'));
      PreviewIframeStylesManager.init(getPreviewIframeWrapperElement());
    }
  }
  componentDidMount() {
    const {
      appStatus,
      iframeReady,
      addModuleSidebarLoaded
    } = this.props;
    if (appStatus === APP_STATUS.READY) {
      this.trackParentAppReadyLifecycle();
      if (iframeReady && addModuleSidebarLoaded) {
        this.triggerPostWholeAppReadyActions();
      }
    }
  }
  componentDidUpdate(prevProps) {
    const {
      appStatus,
      iframeReady,
      addModuleSidebarLoaded,
      shouldHaveAddModuleSidebar,
      hasIframe
    } = this.props;
    if (hasIframe) {
      if (!prevProps.addModuleSidebarLoaded && addModuleSidebarLoaded) {
        trackAppLifecycleTimestamp(SIDEBAR_IFRAME_UX_READY);
      }
      if (!prevProps.iframeReady && iframeReady) {
        trackAppLifecycleTimestamp(PREVIEW_IFRAME_UX_READY);
      }
    }
    if (prevProps.appStatus !== APP_STATUS.READY && appStatus === APP_STATUS.READY) {
      this.trackParentAppReadyLifecycle();
    }
    const sidebarReady = addModuleSidebarLoaded || !shouldHaveAddModuleSidebar;
    const sidebarPreviouslyReady = prevProps.addModuleSidebarLoaded || !prevProps.shouldHaveAddModuleSidebar;
    const previouslyFullyReady = prevProps.appStatus === APP_STATUS.READY && (!hasIframe || prevProps.iframeReady && sidebarPreviouslyReady);
    const fullyReadyNow = appStatus === APP_STATUS.READY && (!hasIframe || iframeReady && sidebarReady);
    if (!previouslyFullyReady && fullyReadyNow) {
      this.setState({
        isAppFullyReady: true
      });
      this.triggerPostWholeAppReadyActions();
    }
  }
  // After the content model is available, I18n is ready, _and_ the this main parent applicatlion
  // has a chance to render of the parent chrome & UI then track `PARENT_APP_UX_READY`
  trackParentAppReadyLifecycle() {
    // Note it is OK if this is called more than once (only the first one ends up tracking a timestamp)
    trackAppLifecycleTimestamp(PARENT_APP_UX_READY);
  }
  triggerPostWholeAppReadyActions() {
    trackAppLifecycleTimestamp(WHOLE_APP_UX_READY);
  }
  renderBaymaxEditor() {
    const {
      appStatus,
      children
    } = this.props;
    const {
      HeaderExtension,
      TabBarExtension,
      SidebarExtension
    } = EditorConfigSingleton.getComponents();
    const shouldRenderCollaborationSidebar = !!EditorConfigSingleton.getIn(['features', 'collaborationSidebar']);
    const editorPaneContainer = /*#__PURE__*/_jsxs(Flex, {
      flexGrow: "1",
      flexShrink: "1",
      flexBasis: "auto",
      overflow: "auto",
      maxInlineSize: "100vw",
      backgroundColor: "fill-surface-default",
      children: [/*#__PURE__*/_jsx(ExitLayoutModeButtonContainer
      // Probably put this into an "extra AppContainerComponents" key
      , {}), /*#__PURE__*/_jsx(SidebarExtension
      // Probably look into refactoring this into a univesal sidebar
      // with "extension" points
      , {}), children]
    });
    return /*#__PURE__*/_jsx(FullScreenEditorWrapper, {
      appStatus: appStatus
      // Look into seeing if this should be universal or custom passed in components
      ,
      headerComponent: /*#__PURE__*/_jsx(HeaderExtension, {}),
      tabBarComponent: /*#__PURE__*/_jsx(TabBarExtension, {}),
      children: /*#__PURE__*/_jsx("div", {
        className: "hs-patagonia-editor patagonia",
        children: /*#__PURE__*/_jsx("div", {
          id: "contentEditorUI-app",
          children: shouldRenderCollaborationSidebar ? /*#__PURE__*/_jsx(CollaborationSidebarWithWrapper, {
            children: editorPaneContainer
          }) : editorPaneContainer
        })
      })
    });
  }
  renderNavMarkers() {
    const {
      iframeReady,
      addModuleSidebarLoaded,
      shouldHaveAddModuleSidebar,
      isReadOnlyMode
    } = this.props;
    return /*#__PURE__*/_jsxs(Fragment, {
      children: [!isReadOnlyMode && iframeReady && /*#__PURE__*/_jsx(NavMarker, {
        name: "INPAGE_EDITOR_READY"
      }), isReadOnlyMode && /*#__PURE__*/_jsx(NavMarker, {
        name: "READ_ONLY_MODE"
      }), shouldHaveAddModuleSidebar && addModuleSidebarLoaded && /*#__PURE__*/_jsx(NavMarker, {
        name: "ADD_MODULE_SIDEBAR_READY"
      }), (!shouldHaveAddModuleSidebar || isReadOnlyMode) && /*#__PURE__*/_jsx(NavMarker, {
        name: "ADD_MODULE_SIDEBAR_NOT_NEEDED"
      })]
    });
  }
  render() {
    const {
      fetchModuleSchemas,
      hasRequiredAccess,
      hasStagingDomain,
      shouldShowCreateNewSectionPanel,
      showDeleteSectionModal,
      showSectionsLibraryModal,
      hasIframe,
      supportsSections,
      isSectionsInstructionalModalOpen,
      addSectionMissingModuleSchemaErrors
    } = this.props;
    const {
      isAppFullyReady
    } = this.state;
    const {
      ExtraApplicationContainerComponents,
      ExtraApplicationContainerEditorWrapper
    } = EditorConfigSingleton.getComponents();
    const {
      autoSave
    } = EditorConfigSingleton.getIn(['redux', 'actions']);
    const {
      userAttributesToFetch = null
    } = EditorConfigSingleton.get('api');
    const pageClassName = hasStagingDomain ? 'staging-domain' : "";
    const UniversalApplicationContainerEditorWrapper = ExtraApplicationContainerEditorWrapper || Fragment;
    return /*#__PURE__*/_jsx(ApplicationContainer, {
      hasSkeleton: true,
      pageClassName: pageClassName,
      hasRequiredAccess: hasRequiredAccess,
      fetchModuleSchemas: fetchModuleSchemas,
      userAttributesToFetch: userAttributesToFetch,
      ErrorWrapperComponent: ErrorPagePortal,
      shouldTrackLoadError: true,
      children: /*#__PURE__*/_jsxs(UniversalApplicationContainerEditorWrapper, {
        children: [this.renderBaymaxEditor(), this.renderNavMarkers(), /*#__PURE__*/_jsx(CoreEditorComponents, {
          isAppFullyReady: isAppFullyReady,
          autoSaveAction: autoSave,
          hasIframe: hasIframe,
          supportsSections: supportsSections
        }), supportsSections && /*#__PURE__*/_jsx(AsyncComponentContext.Consumer, {
          children: ({
            CreateNewSectionPanel,
            DeleteSavedSectionModal,
            SectionsLibraryModal
          }) => {
            return /*#__PURE__*/_jsxs(Fragment, {
              children: [showDeleteSectionModal && /*#__PURE__*/_jsx(DeleteSavedSectionModal, {}), shouldShowCreateNewSectionPanel && /*#__PURE__*/_jsx(CreateNewSectionPanel, {
                autoSave: autoSave
              }), showSectionsLibraryModal && /*#__PURE__*/_jsx(SectionsLibraryModal, {})]
            });
          }
        }), ExtraApplicationContainerComponents && /*#__PURE__*/_jsx(ExtraApplicationContainerComponents, {}), addSectionMissingModuleSchemaErrors > 0 && /*#__PURE__*/_jsx(AddSectionModuleSchemaMissingErrorModal, {
          missingModuleSchemaErrorCount: addSectionMissingModuleSchemaErrors
        }), supportsSections && isSectionsInstructionalModalOpen && /*#__PURE__*/_jsx(AsyncComponentContext.Consumer, {
          children: ({
            SectionsInstructionModal
          }) => {
            UsageTracker.trackEditorInteraction({
              action: 'Sections Action',
              event: 'opened-create-sections-instruction-modal'
            });
            return /*#__PURE__*/_jsx(SectionsInstructionModal, {
              closeModal: this.props.closeModal
            });
          }
        }), /*#__PURE__*/_jsx(ContentAssistanceRequestDelegate, {})]
      })
    });
  }
}
UniversalApplicationContainer.propTypes = {
  children: PropTypes.node,
  fetchModuleSchemas: PropTypes.func.isRequired,
  hasRequiredAccess: PropTypes.bool,
  hasStagingDomain: PropTypes.bool,
  appStatus: PropTypes.string.isRequired,
  iframeReady: PropTypes.bool.isRequired,
  addModuleSidebarLoaded: PropTypes.bool.isRequired,
  shouldHaveAddModuleSidebar: PropTypes.bool.isRequired,
  isReadOnlyMode: PropTypes.bool.isRequired,
  shouldShowCreateNewSectionPanel: PropTypes.bool.isRequired,
  showDeleteSectionModal: PropTypes.bool.isRequired,
  router: PropTypes.shape({
    push: PropTypes.func.isRequired,
    replace: PropTypes.func.isRequired
  }),
  hasIframe: PropTypes.bool.isRequired,
  supportsSections: PropTypes.bool.isRequired,
  isSectionsInstructionalModalOpen: PropTypes.bool.isRequired,
  closeModal: PropTypes.func.isRequired,
  addSectionMissingModuleSchemaErrors: PropTypes.number
};
UniversalApplicationContainer.defaultProps = {
  addSectionMissingModuleSchemaErrors: 0
};
export default connect(mapStateToProps, mapDispatchToProps)(UniversalApplicationContainer);